import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Lock,
  ChevronRight,
  Check,
  Circle,
  Loader2,
  AlertTriangle,
  XCircle,
  CheckCircle2,
  Shield,
} from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TwoFactorSettings from '../components/TwoFactorSettings';
import { usePasswordPolicy, evaluatePassword } from '../hooks/usePasswordPolicy';
import PasswordRequirements from '../components/common/PasswordRequirements';

interface Profile {
  id: number;
  email: string;
  name: string;
  two_fa_enabled?: boolean;
  two_fa_method?: string;
}

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
const [smsSent, setSmsSent] = useState(false);
const [smsSending, setSmsSending] = useState(false);


  // Profile state
  const [profile, setProfile] = useState<Profile | null>(null);
  const [name, setName] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Button hover states
  const [saveProfileHovered, setSaveProfileHovered] = useState(false);
  const [changePasswordHovered, setChangePasswordHovered] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteTwoFaCode, setDeleteTwoFaCode] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteHovered, setDeleteHovered] = useState(false);

  const [prereqs, setPrereqs] = useState<{
    workspacesOk: boolean;
    agencyOk: boolean;
    workspaceCount: number;
    workspacesByAgency: { agencyName: string; count: number }[];
    agencyIssue: string | null;
    memberAgencies: string[];
    loaded: boolean;
  }>({
    workspacesOk: false,
    agencyOk: false,
    workspaceCount: 0,
    workspacesByAgency: [],
    agencyIssue: null,
    memberAgencies: [],
    loaded: false,
  });

const { policy: passwordPolicy } = usePasswordPolicy();
  const { agencyId } = useAuth();

  useEffect(() => {
    const loadPrereqs = async () => {
      try {
        const [profileRes, wsRes, allAgenciesRes] = await Promise.all([
          apiService.get('/profile'),
          apiService.getWorkspaces(undefined),
          apiService.get('/agencies/list').catch(() => null),
        ]);
        const currentUserId = profileRes?.data?.user?.id;
        const allAgencies = allAgenciesRes?.data?.agencies || [];
        const activeWorkspaces = (wsRes || []).filter(
          (w: any) => w.status !== 'deleted' && Number(w.owner_id) === Number(currentUserId)
        );
        const workspacesOk = activeWorkspaces.length === 0;

        const workspacesByAgency: { agencyName: string; count: number }[] = [];
        if (!workspacesOk && allAgencies.length > 0) {
          const agencyMap: { [id: string]: string } = {};
          allAgencies.forEach((a: any) => { agencyMap[a.agency_id] = a.name; });
          const grouped: { [id: string]: number } = {};
          activeWorkspaces.forEach((w: any) => {
            const key = w.agency_id || 'unknown';
            grouped[key] = (grouped[key] || 0) + 1;
          });
          Object.entries(grouped).forEach(([agencyId, count]) => {
            workspacesByAgency.push({ agencyName: agencyMap[agencyId] || agencyId, count });
          });
        }

        let agencyOk = true;
        let agencyIssue = null;
        const ownedAgencies = allAgencies.filter((a: any) => a.role === 'owner');
        for (const owned of ownedAgencies) {
          if ((owned.member_count || 0) > 1) {
            agencyOk = false;
            const others = owned.member_count - 1;
            agencyIssue = `You own ${ownedAgencies.length > 1 ? `"${owned.name}"` : 'an agency'} with ${others} other member${others > 1 ? 's' : ''} — transfer ownership first`;
            break;
          }
        }

        const memberAgencies = allAgencies
          .filter((a: any) => a.role !== 'owner')
          .map((a: any) => a.name);

        setPrereqs({
          workspacesOk,
          agencyOk,
          workspaceCount: activeWorkspaces.length,
          workspacesByAgency,
          agencyIssue,
          memberAgencies,
          loaded: true,
        });
      } catch {
        setPrereqs((p) => ({ ...p, loaded: true }));
      }
    };
    loadPrereqs();
  }, []);

  const handleDeleteAccount = async () => {
    if (!deletePassword) { showError('Password is required'); return; }
    if (profile?.two_fa_enabled && !deleteTwoFaCode) {
      showError('Two-factor authentication code is required');
      return;
    }
    setDeleteLoading(true);
    try {
      const payload: { password: string; two_fa_code?: string } = { password: deletePassword };
      if (profile?.two_fa_enabled) {
        payload.two_fa_code = deleteTwoFaCode;
      }
      await apiService.post('/profile/delete', payload);
      showSuccess('Your account has been deleted');
      navigate('/login');
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('workspaces')) {
        showError('You must delete all your workspaces before deleting your account');
      } else if (msg.includes('agency')) {
        showError('You must transfer ownership or remove all agency members first');
      } else if (msg.includes('two-factor') || msg.includes('2FA') || msg.includes('TWO_FA')) {
        showError('Invalid two-factor authentication code');
      } else {
        showError(msg || 'Failed to delete account');
      }
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletePassword('');
    setDeleteTwoFaCode('');
setSmsSent(false);
setSmsSending(false);
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await apiService.get('/profile');
      setProfile(response.data.user);
      setName(response.data.user.name || '');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load profile');
    }
  };

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      await apiService.put('/profile', { name });
      showSuccess('Profile updated successfully');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      showError('Passwords do not match');
      return;
    }
if (Array.from(newPassword).length < passwordPolicy.minLength) {
      showError(`Password must be at least ${passwordPolicy.minLength} characters`);
      return;
    }
    setLoading(true);
    try {
      await apiService.put('/profile/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      showSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

// Password validation — rules come from the server policy, not hardcoded.
  // Note: breach screening and name/email screening run server-side only, so
  // passing these checks does not guarantee the server will accept it.
  const { meetsClientChecks: passwordValid } = evaluatePassword(newPassword, passwordPolicy);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-primary)';
    e.target.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.style.borderColor = 'var(--v2-border)';
    e.target.style.boxShadow = 'none';
  };

  const buttonStyle = (
    primary: boolean,
    hovered: boolean,
    disabled: boolean
  ): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: disabled
      ? 'var(--v2-border)'
      : primary
        ? hovered
          ? 'var(--v2-primary-hover)'
          : 'var(--v2-primary)'
        : hovered
          ? 'var(--v2-surface-hover)'
          : 'var(--v2-surface)',
    color: disabled ? 'var(--v2-text-muted)' : primary ? '#ffffff' : 'var(--v2-text)',
    border: primary ? 'none' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  });

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.875rem',
    color: 'var(--v2-text)',
    backgroundColor: 'var(--v2-surface)',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: '16px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '6px',
  };

  // Determine 2FA label for the delete modal
  const twoFaMethodLabel = () => {
    if (!profile?.two_fa_method) return 'Authenticator code';
    if (profile.two_fa_method === 'sms') return 'SMS verification code';
    if (profile.two_fa_method === 'both') return 'Authenticator or SMS code';
    return 'Authenticator code';
  };

  const deleteConfirmDisabled =
    deleteLoading ||
    !deletePassword ||
    (!!profile?.two_fa_enabled && !deleteTwoFaCode);

  return (
    <div style={{ padding: '32px', maxWidth: '800px', fontFamily: 'var(--font-sans)' }}>
      {/* Breadcrumbs */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '0.875rem',
          color: 'var(--v2-text-tertiary)',
          marginBottom: '24px',
        }}
      >
        <span
          onClick={() => navigate('/dashboard')}
          style={{ cursor: 'pointer' }}
          onMouseOver={(e) => (e.currentTarget.style.color = 'var(--v2-primary)')}
          onMouseOut={(e) => (e.currentTarget.style.color = 'var(--v2-text-tertiary)')}
        >
          Workspaces
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ color: 'var(--v2-text)' }}>Profile & Security</span>
      </div>

      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--v2-text)', margin: '0 0 32px 0' }}>
        Profile & Security
      </h1>

      {/* Profile Information */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          boxShadow: 'var(--shadow-sm)',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--v2-primary-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <User size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          </div>
          <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
            Profile Information
          </h2>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Full Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '10px 12px',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              color: 'var(--v2-text)',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Email Address</label>
          <input
            type="email"
            value={profile?.email || ''}
            disabled
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '10px 12px',
              backgroundColor: 'var(--v2-surface-alt)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              color: 'var(--v2-text-tertiary)',
              cursor: 'not-allowed',
              boxSizing: 'border-box',
            }}
          />
          <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', marginTop: '6px' }}>
            Contact support to change your email address
          </p>
        </div>

        <button
          onClick={handleUpdateProfile}
          disabled={loading}
          onMouseOver={() => setSaveProfileHovered(true)}
          onMouseOut={() => setSaveProfileHovered(false)}
          style={buttonStyle(true, saveProfileHovered, loading)}
        >
          {loading && <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
          {loading ? 'Saving...' : 'Save Profile'}
        </button>
      </div>

      {/* Change Password */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          boxShadow: 'var(--shadow-sm)',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--v2-warning-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Lock size={20} strokeWidth={1.5} color="#d97706" />
          </div>
          <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
            Change Password
          </h2>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Current Password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '10px 12px',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              color: 'var(--v2-text)',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '10px 12px',
              backgroundColor: 'var(--v2-surface)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              color: 'var(--v2-text)',
              outline: 'none',
              boxSizing: 'border-box',
              transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
            }}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
<PasswordRequirements
            password={newPassword}
            policy={passwordPolicy}
            columns={1}
            style={{ maxWidth: '400px' }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Confirm New Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{
              width: '100%',
              maxWidth: '400px',
              padding: '10px 12px',
              backgroundColor: 'var(--v2-surface)',
              border: `1px solid ${
                confirmPassword.length > 0
                  ? passwordsMatch
                    ? 'var(--v2-success)'
                    : 'var(--v2-error)'
                  : 'var(--v2-border)'
              }`,
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              color: 'var(--v2-text)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          {confirmPassword.length > 0 && (
            <p
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontSize: '0.8125rem',
                color: passwordsMatch ? 'var(--v2-success)' : 'var(--v2-error)',
                marginTop: '6px',
              }}
            >
              {passwordsMatch && <Check size={14} strokeWidth={2.5} />}
              {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
            </p>
          )}
        </div>

        <button
          onClick={handleChangePassword}
          disabled={loading || !currentPassword || !passwordValid || !passwordsMatch}
          onMouseOver={() => setChangePasswordHovered(true)}
          onMouseOut={() => setChangePasswordHovered(false)}
          style={buttonStyle(false, changePasswordHovered, loading || !currentPassword || !passwordValid || !passwordsMatch)}
        >
          {loading ? 'Changing...' : 'Change Password'}
        </button>
      </div>

      {/* Two-Factor Authentication */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          boxShadow: 'var(--shadow-sm)',
          padding: '24px',
        }}
      >
        <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 20px 0' }}>
          Two-Factor Authentication
        </h2>
        <TwoFactorSettings onUpdate={loadProfile} />
      </div>

{/* Cookie Preferences */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          boxShadow: 'var(--shadow-sm)',
          padding: '24px',
          marginTop: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--v2-primary-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Shield size={20} strokeWidth={1.5} color="var(--v2-primary)" />
          </div>
          <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
            Cookie Preferences
          </h2>
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-muted)', marginBottom: '16px' }}>
          Manage your cookie and tracking preferences, including analytics and functional cookies.
        </p>
        <button
          onClick={() => (window as any).concord?.plugin?.widget?.setOpen(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: 'var(--v2-surface)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            color: 'var(--v2-text)',
          }}
        >
          Manage Cookie Preferences
        </button>
      </div>

      {/* Danger Zone */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-error)',
          boxShadow: 'var(--shadow-sm)',
          padding: '24px',
          marginTop: '24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--v2-error-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AlertTriangle size={20} strokeWidth={1.5} color="var(--v2-error)" />
          </div>
          <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--v2-error)', margin: 0 }}>
            Delete Account
          </h2>
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-muted)', marginBottom: '16px' }}>
          Permanently delete your account and all associated data. This action cannot be undone.
        </p>

        {/* Prerequisites checklist */}
        <div
          style={{
            backgroundColor: 'var(--v2-surface-alt)',
            border: '1px solid var(--v2-border)',
            borderRadius: 'var(--radius-md)',
            padding: '16px',
            marginBottom: '20px',
          }}
        >
          <p style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 12px 0' }}>
            Before you can delete your account:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {/* Workspaces */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              {!prereqs.loaded ? (
                <Loader2 size={16} strokeWidth={1.5} color="var(--v2-text-muted)" style={{ flexShrink: 0, marginTop: '1px' }} />
              ) : prereqs.workspacesOk ? (
                <CheckCircle2 size={16} strokeWidth={1.5} color="var(--v2-success)" style={{ flexShrink: 0, marginTop: '1px' }} />
              ) : (
                <XCircle size={16} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
              )}
              <div>
                <span
                  style={{
                    fontSize: '0.875rem',
                    color: prereqs.workspacesOk ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                    textDecoration: prereqs.workspacesOk ? 'line-through' : 'none',
                  }}
                >
                  Delete or transfer all workspaces
                </span>
                {!prereqs.workspacesOk && prereqs.loaded && (
                  <div style={{ marginTop: '4px' }}>
                    {prereqs.workspacesByAgency.map(({ agencyName, count }) => (
                      <div key={agencyName} style={{ fontSize: '0.8125rem', color: 'var(--v2-error)' }}>
                        {count} workspace{count !== 1 ? 's' : ''} in <strong>{agencyName}</strong> — switch to that agency and delete or transfer them
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Agency ownership */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              {!prereqs.loaded ? (
                <Loader2 size={16} strokeWidth={1.5} color="var(--v2-text-muted)" style={{ flexShrink: 0, marginTop: '1px' }} />
              ) : prereqs.agencyOk ? (
                <CheckCircle2 size={16} strokeWidth={1.5} color="var(--v2-success)" style={{ flexShrink: 0, marginTop: '1px' }} />
              ) : (
                <XCircle size={16} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
              )}
              <div>
                <span
                  style={{
                    fontSize: '0.875rem',
                    color: prereqs.agencyOk ? 'var(--v2-text-muted)' : 'var(--v2-text)',
                    textDecoration: prereqs.agencyOk ? 'line-through' : 'none',
                  }}
                >
                  Resolve agency ownership
                </span>
                {!prereqs.agencyOk && prereqs.agencyIssue && (
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', marginLeft: '8px' }}>
                    {prereqs.agencyIssue} —{' '}
                    <span
                      onClick={() => navigate('/dashboard/agency')}
                      style={{ textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      go to Agency
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <button
onClick={() => {
  setShowDeleteModal(true);
}}
          disabled={!prereqs.loaded || !prereqs.workspacesOk || !prereqs.agencyOk}
          onMouseOver={() => setDeleteHovered(true)}
          onMouseOut={() => setDeleteHovered(false)}
          style={{
            padding: '10px 20px',
            backgroundColor:
              !prereqs.loaded || !prereqs.workspacesOk || !prereqs.agencyOk
                ? 'var(--v2-border)'
                : deleteHovered
                  ? '#b91c1c'
                  : 'var(--v2-error)',
            color:
              !prereqs.loaded || !prereqs.workspacesOk || !prereqs.agencyOk
                ? 'var(--v2-text-muted)'
                : '#fff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor:
              !prereqs.loaded || !prereqs.workspacesOk || !prereqs.agencyOk
                ? 'not-allowed'
                : 'pointer',
            transition: 'all 0.15s',
          }}
        >
          Delete My Account
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--v2-surface)',
              borderRadius: 'var(--radius-lg)',
              padding: '32px',
              maxWidth: '440px',
              width: '100%',
              margin: '0 16px',
            }}
          >
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--v2-error)', margin: '0 0 12px 0' }}>
              Delete Account
            </h3>
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-muted)', marginBottom: prereqs.memberAgencies.length > 0 ? '12px' : '20px' }}>
              This action is permanent and cannot be undone. Enter your password to confirm.
            </p>

            {prereqs.memberAgencies.length > 0 && (
              <div
                style={{
                  backgroundColor: 'var(--v2-surface-alt)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 14px',
                  marginBottom: '20px',
                  fontSize: '0.8125rem',
                  color: 'var(--v2-text-muted)',
                }}
              >
                You will also be removed from{' '}
                {prereqs.memberAgencies.length === 1 ? 'this agency' : 'these agencies'}:{' '}
                <strong style={{ color: 'var(--v2-text)' }}>{prereqs.memberAgencies.join(', ')}</strong>
              </div>
            )}

            {/* Password field */}
            <label style={labelStyle}>Password</label>
            <input
              type="password"
              placeholder="Enter your password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              style={inputStyle}
            />

            {/* 2FA field — shown only when 2FA is enabled */}
{profile?.two_fa_enabled && (
              <>
                <label style={labelStyle}>{twoFaMethodLabel()}</label>

                {/* SMS: show send button first, then reveal input after sending */}
                {(profile.two_fa_method === 'sms' || profile.two_fa_method === 'both') && !smsSent && (
                  <button
                    onClick={async () => {
                      setSmsSending(true);
                      try {
                        await apiService.post('/profile/2fa/send-sms', {});
                        setSmsSent(true);
                      } catch {
                        showError('Failed to send SMS code');
                      } finally {
                        setSmsSending(false);
                      }
                    }}
                    disabled={smsSending}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: 'var(--v2-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem',
                      cursor: smsSending ? 'not-allowed' : 'pointer',
                      opacity: smsSending ? 0.7 : 1,
                    }}
                  >
                    {smsSending ? 'Sending...' : 'Send verification code via SMS'}
                  </button>
                )}

                {/* Show code input: always for app, only after SMS sent for sms/both */}
                {(profile.two_fa_method === 'app' || smsSent) && (
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={profile.two_fa_method === 'sms' ? '6-digit SMS code' : '6-digit code'}
                    value={deleteTwoFaCode}
                    onChange={(e) => setDeleteTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    style={inputStyle}
                  />
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCloseDeleteModal}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'var(--v2-surface)',
                  border: '1px solid var(--v2-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmDisabled}
                style={{
                  padding: '10px 20px',
                  backgroundColor: deleteConfirmDisabled ? 'var(--v2-border)' : 'var(--v2-error)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: deleteConfirmDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                {deleteLoading ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ProfilePage;
